'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { ChevronLeft, Settings, User, Sun, Moon } from 'lucide-react'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { toast } from '@/components/ui/ToastContainer'
import { useTheme } from '@/contexts/ThemeContext'
import { parseAuthToken } from '@/utils/base64'

export default function SettingsPage() {
  const router = useRouter()
  const { theme, toggleTheme } = useTheme()
  const [userProfile, setUserProfile] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('auth_token')

      if (!token) {
        router.push('/login')
        return
      }

      const decoded = parseAuthToken(token)
      if (!decoded) {
        console.error('Token parsing failed')
        localStorage.removeItem('auth_token')
        router.push('/login')
        return
      }

      const supabase = getSupabaseClient()

      // 카카오 또는 네이버 로그인인지 확인하여 조회
      let query = supabase
        .from('user_profiles')
        .select('*')

      if (decoded.kakao_id) {
        query = query.eq('kakao_id', decoded.kakao_id)
      } else if (decoded.naver_id) {
        query = query.eq('naver_id', decoded.naver_id)
      } else {
        console.error('No kakao_id or naver_id in token')
        localStorage.removeItem('auth_token')
        router.push('/login')
        return
      }

      const { data, error } = await query.single()

      if (error || !data) {
        console.error('Profile not found:', error)
        // 프로필이 없으면 (탈퇴한 사용자) 토큰 제거하고 홈으로 리다이렉트
        localStorage.removeItem('auth_token')
        window.location.href = '/'
        return
      }

      setUserProfile(data)
    } catch (error) {
      console.error('Error loading profile:', error)
      toast.error('프로필 로드 중 오류가 발생했습니다')
      router.push('/login')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (!userProfile) return

    const confirmed = window.confirm(
      '정말로 회원탈퇴를 하시겠습니까?\n\n탈퇴 시 모든 데이터가 삭제되며 복구할 수 없습니다.'
    )

    if (!confirmed) return

    try {
      const kakaoId = userProfile.kakao_id
      const naverId = userProfile.naver_id

      // 새로운 DELETE API 사용 (Service Role로 확실한 삭제)
      const response = await fetch('/api/delete-account', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          kakao_id: kakaoId,
          naver_id: naverId
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'API 요청 실패')
      }

      if (!result.success) {
        throw new Error('회원탈퇴 처리에 실패했습니다.')
      }

      // 완전한 캐시 및 데이터 정리
      localStorage.clear()
      sessionStorage.clear()

      // 쿠키 정리 (인증 관련)
      if (typeof document !== 'undefined') {
        document.cookie.split(";").forEach((c) => {
          const eqPos = c.indexOf("=")
          const name = eqPos > -1 ? c.substr(0, eqPos) : c
          document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
        })
      }

      // 브라우저 캐시 새로고침 강제 (선택적)
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys()
          await Promise.all(cacheNames.map(name => caches.delete(name)))
        } catch {
          // 캐시 삭제 실패는 무시
        }
      }

      // 페이지 새로고침으로 완전한 상태 초기화
      window.location.href = '/'
    } catch (error: any) {
      toast.error(`회원탈퇴 처리 중 오류가 발생했습니다: ${error.message}`)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0f172a] py-8 xxs:py-4">
        <div className="max-w-2xl mx-auto px-4 xxs:px-2">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded mb-8"></div>
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-16 bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`min-h-screen py-8 xxs:py-4 ${
      theme === 'light'
        ? 'bg-[#f4f7fb]'
        : 'bg-[#0f172a]'
    }`}>
      <div className="max-w-2xl mx-auto px-4 xxs:px-2">
        {/* 헤더 */}
        <div className="flex items-center gap-4 xxs:gap-2 mb-8 xxs:mb-4">
          <button
            onClick={() => router.back()}
            className={`p-2 rounded-lg transition-colors ${
              theme === 'light'
                ? 'bg-white text-gray-800 hover:bg-gray-100 border border-gray-200'
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            <ChevronLeft className="w-5 h-5 xxs:w-4 xxs:h-4" />
          </button>
          <h1 className="text-2xl xxs:text-lg font-bold text-white flex items-center gap-2">
            <Settings className="w-6 h-6 xxs:w-5 xxs:h-5" />
            설정
          </h1>
        </div>

        <div className="space-y-6 xxs:space-y-3">
          {/* 프로필 정보 */}
          <div className={`rounded-2xl p-6 xxs:p-3 ${
            theme === 'light'
              ? 'bg-white border border-gray-200 shadow-lg'
              : 'bg-gray-800'
          }`}>
            <h2 className={`text-lg xxs:text-base font-semibold mb-4 flex items-center gap-2 ${
              theme === 'light' ? 'text-gray-800' : 'text-white'
            }`}>
              <User className="w-5 h-5 xxs:w-4 xxs:h-4" />
              프로필 정보
            </h2>
            
            <div className="space-y-4">
              <div className={`flex items-center justify-between gap-3 py-3 border-b ${
                theme === 'light' ? 'border-gray-200' : 'border-gray-700'
              }`}>
                <span className={theme === 'light' ? 'text-gray-600' : 'text-gray-300'}>이름</span>
                <span className={`font-medium truncate text-right ${theme === 'light' ? 'text-gray-800' : 'text-white'}`}>{userProfile?.name}</span>
              </div>

              <div className={`flex items-center justify-between gap-3 py-3 border-b ${
                theme === 'light' ? 'border-gray-200' : 'border-gray-700'
              }`}>
                <span className={`flex-shrink-0 ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>인증 레벨</span>
                <span className="text-[#2b6df0] dark:text-[#5ea6ff] font-medium truncate text-right">{userProfile?.certification_level}</span>
              </div>

              <div className={`flex items-center justify-between gap-3 py-3 border-b ${
                theme === 'light' ? 'border-gray-200' : 'border-gray-700'
              }`}>
                <span className={`flex-shrink-0 ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>지역</span>
                <span className={`font-medium truncate text-right ${theme === 'light' ? 'text-gray-800' : 'text-white'}`}>{userProfile?.location || '미설정'}</span>
              </div>
              
              <div className="flex items-center justify-between gap-3 py-3">
                <span className={theme === 'light' ? 'text-gray-600' : 'text-gray-300'}>프리미엄 회원</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  userProfile?.is_premium 
                    ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-black'
                    : theme === 'light' 
                      ? 'bg-gray-200 text-gray-700'
                      : 'bg-gray-700 text-gray-300'
                }`}>
                  {userProfile?.is_premium ? '프리미엄' : '기본'}
                </span>
              </div>
            </div>

            <button
              onClick={() => router.push('/profile/edit')}
              className="w-full mt-4 bg-[#0a1f3a] text-white py-2 xxs:py-1.5 px-4 xxs:px-3 rounded-xl transition-colors"
            >
              프로필 수정하기
            </button>
          </div>

          {/* 테마 설정 */}
          <div className={`rounded-2xl p-6 xxs:p-3 ${
            theme === 'light'
              ? 'bg-white border border-gray-200 shadow-lg'
              : 'bg-gray-800'
          }`}>
            <h2 className={`text-lg xxs:text-base font-semibold mb-4 flex items-center gap-2 ${
              theme === 'light' ? 'text-gray-800' : 'text-white'
            }`}>
              {theme === 'light' ? <Sun className="w-5 h-5 xxs:w-4 xxs:h-4" /> : <Moon className="w-5 h-5 xxs:w-4 xxs:h-4" />}
              테마 설정
            </h2>
            
            <div className="flex items-center justify-between gap-3 py-3">
              <div>
                <div className={`font-medium ${theme === 'light' ? 'text-gray-800' : 'text-white'}`}>테마 모드</div>
                <div className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>밝은 테마와 어두운 테마를 선택할 수 있습니다</div>
              </div>
              <button
                onClick={toggleTheme}
                className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors duration-200 focus:outline-none ${
                  theme === 'light' ? 'bg-[#2b6df0]' : 'bg-[#1e3a5f]'
                }`}
              >
                <span
                  className={`inline-block w-4 h-4 rounded-full bg-white shadow-lg transform transition-transform duration-200 ${
                    theme === 'light' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            <div className={`mt-4 p-3 rounded-lg ${
              theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'
            }`}>
              <div className="flex items-center gap-2 text-sm">
                {theme === 'light' ? (
                  <>
                    <Sun className="w-4 h-4 xxs:w-3 xxs:h-3 text-yellow-500" />
                    <span className={theme === 'light' ? 'text-gray-800' : 'text-white'}>현재: 밝은 테마</span>
                  </>
                ) : (
                  <>
                    <Moon className="w-4 h-4 xxs:w-3 xxs:h-3 text-blue-400" />
                    <span className={theme !== 'dark' ? 'text-gray-800' : 'text-white'}>현재: 어두운 테마</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* 회원탈퇴 */}
          <div className="pt-8 xxs:pt-4">
            <button
              onClick={handleDeleteAccount}
              className={`text-sm ${
                theme === 'light' ? 'text-gray-400 hover:text-red-500' : 'text-gray-500 hover:text-red-400'
              } transition-colors`}
            >
              회원탈퇴
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
